<script setup>
import { computed, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  loadDefaultActionButtons,
  normalizeDefaultButtons,
  saveDefaultActionButtons,
} from 'dashboard/helper/telegramCampaignDefaults';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  accountId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['close', 'saved']);

const buttons = ref([]);

const canAdd = computed(() => buttons.value.length < 8);

const closeDialog = () => emit('close');

const loadButtons = () => {
  buttons.value = loadDefaultActionButtons(props.accountId).map(button => ({
    text: button.text,
    url: button.url,
    openInAppPopup: Boolean(button.openInAppPopup),
  }));
  if (!buttons.value.length) {
    buttons.value = [{ text: '', url: '', openInAppPopup: false }];
  }
};

const addButton = () => {
  if (!canAdd.value) return;
  buttons.value.push({ text: '', url: '', openInAppPopup: false });
};

const removeButton = index => {
  buttons.value.splice(index, 1);
  if (!buttons.value.length) {
    buttons.value = [{ text: '', url: '', openInAppPopup: false }];
  }
};

const saveButtons = () => {
  const normalized = normalizeDefaultButtons(buttons.value);
  saveDefaultActionButtons(props.accountId, normalized);
  emit('saved', normalized);
  closeDialog();
};

watch(
  () => props.show,
  visible => {
    if (visible) loadButtons();
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="show" class="default-buttons-overlay">
    <div class="default-buttons-dialog">
      <header class="default-buttons-dialog__header">
        <h3 class="text-base font-semibold text-n-slate-12">
          Default action buttons
        </h3>
        <button class="close-btn" @click="closeDialog">
          <span class="i-ph-x-bold size-4" />
        </button>
      </header>

      <div class="default-buttons-dialog__content">
        <div
          v-for="(button, index) in buttons"
          :key="`default-btn-${index}`"
          class="button-row"
        >
          <Input v-model="button.text" placeholder="Button text" />
          <Input v-model="button.url" placeholder="Button URL (https://...)" />
          <Button
            icon="i-ph-trash-bold"
            color="ruby"
            variant="faded"
            size="sm"
            @click="removeButton(index)"
          />
          <label class="button-row__webapp">
            <input
              v-model="button.openInAppPopup"
              type="checkbox"
              class="w-4 h-4 rounded border-n-slate-7 text-n-blue-9 focus:ring-n-blue-9"
            />
            <span>Open in app pop-up</span>
          </label>
        </div>
      </div>

      <footer class="default-buttons-dialog__footer">
        <button v-if="canAdd" class="add-button-link" @click="addButton">
          <span class="i-ph-plus-bold size-4" />
          Add button
        </button>
        <div class="actions">
          <Button variant="faded" color="slate" @click="closeDialog">
            Cancel
          </Button>
          <Button color="blue" @click="saveButtons">Save</Button>
        </div>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.default-buttons-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
}

.default-buttons-dialog {
  width: min(860px, calc(100vw - 2rem));
  max-height: calc(100vh - 2rem);
  border-radius: 14px;
  border: 1px solid var(--wlab-border-default);
  background: var(--wlab-bg-primary);
  display: flex;
  flex-direction: column;
}

.default-buttons-dialog__header {
  height: 52px;
  padding: 0 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--wlab-text-muted);
  cursor: pointer;
}

.default-buttons-dialog__content {
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.button-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 0.5rem;
  align-items: start;
}

.button-row__webapp {
  grid-column: 1 / -1;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--wlab-text-secondary);
}

.default-buttons-dialog__footer {
  border-top: 1px solid #e5e7eb;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.add-button-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: none;
  background: transparent;
  color: var(--wlab-text-link);
  font-size: 0.875rem;
  cursor: pointer;
}

.actions {
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
}
</style>
